import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/api/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateVendor } from '@/api/hooks/useVendors';
import type { VendorTier } from '@/api/types/vendor.types';

const INDUSTRIES = [
  'Technology', 'Financial Services', 'Healthcare', 'Manufacturing',
  'Retail', 'Telecommunications', 'Energy', 'Government', 'Education',
  'Professional Services', 'Media', 'Transportation', 'Real Estate', 'Other',
];

const TIERS: { value: VendorTier; label: string; description: string }[] = [
  { value: 'tier_1_critical', label: 'Tier 1 — Critical', description: 'Mission-critical, high data access, significant operational dependency' },
  { value: 'tier_2_high', label: 'Tier 2 — High', description: 'Important vendors with moderate risk exposure' },
  { value: 'tier_3_medium', label: 'Tier 3 — Medium', description: 'Standard vendors with limited data access' },
  { value: 'tier_4_low', label: 'Tier 4 — Low', description: 'Low-risk, minimal data access, easily replaceable' },
];

interface AddVendorModalProps {
  open: boolean;
  onClose: () => void;
}

export function AddVendorModal({ open, onClose }: AddVendorModalProps) {
  const createVendor = useCreateVendor();

  const [form, setForm] = useState({
    name: '',
    website: '',
    primary_domain: '',
    industry: '',
    tier: '' as VendorTier | '',
    country: '',
    description: '',
  });

  const [contact, setContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    title: '',
  });

  const [submitting, setSubmitting] = useState(false);

  function set(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function setC(field: string, value: string) {
    setContact((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name || !form.tier || !form.primary_domain || submitting) return;
    setSubmitting(true);
    try {
      const vendor = await createVendor.mutateAsync({
        name: form.name,
        website: form.website || undefined,
        industry: form.industry || undefined,
        tier: form.tier as VendorTier,
        country: form.country || undefined,
        description: form.description || undefined,
        metadata: form.primary_domain ? { primary_domain: form.primary_domain } : {},
      });
      // Create primary contact if fields are filled
      if (contact.first_name && contact.last_name && contact.email) {
        try {
          await apiClient.post(`/vendors/${vendor.id}/contacts`, {
            firstName: contact.first_name,
            lastName: contact.last_name,
            email: contact.email,
            title: contact.title || undefined,
          });
        } catch { /* non-fatal — vendor was created successfully */ }
      }
      setForm({ name: '', website: '', primary_domain: '', industry: '', tier: '', country: '', description: '' });
      setContact({ first_name: '', last_name: '', email: '', title: '' });
      onClose();
    } catch { /* createVendor error handled by hook's onError */ } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Vendor</DialogTitle>
          <DialogDescription>
            Add a new third-party vendor to your GRC portfolio.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2" noValidate>
          {/* Organization */}
          <div className="space-y-1.5">
            <Label htmlFor="v-name">Organization name <span className="text-accent-rose">*</span></Label>
            <Input id="v-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Acme Corp" required />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="v-website">Website URL</Label>
              <Input id="v-website" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://acmecorp.com" type="url" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-domain">Primary domain <span className="text-accent-rose">*</span></Label>
              <Input id="v-domain" value={form.primary_domain} onChange={(e) => set('primary_domain', e.target.value)} placeholder="acmecorp.com" required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="v-industry">Industry</Label>
              <select
                id="v-industry"
                value={form.industry}
                onChange={(e) => set('industry', e.target.value)}
                className="flex h-9 w-full rounded border border-border-subtle bg-bg-surface-2 px-3 py-1 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
              >
                <option value="">Select industry</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-country">HQ Country</Label>
              <Input id="v-country" value={form.country} onChange={(e) => set('country', e.target.value.toUpperCase().slice(0, 2))} placeholder="US" maxLength={2} />
              <p className="text-xs text-text-secondary">2-letter ISO code</p>
            </div>
          </div>

          {/* Tier */}
          <div className="space-y-2">
            <Label>Vendor tier <span className="text-accent-rose">*</span></Label>
            <div className="space-y-2">
              {TIERS.map((t) => (
                <label
                  key={t.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                    form.tier === t.value
                      ? 'border-accent-cyan bg-accent-cyan-dim/30'
                      : 'border-border-subtle hover:bg-bg-surface-2'
                  }`}
                >
                  <input
                    type="radio"
                    name="tier"
                    value={t.value}
                    checked={form.tier === t.value}
                    onChange={() => set('tier', t.value)}
                    className="mt-0.5 accent-accent-cyan"
                  />
                  <div>
                    <p className="text-sm font-medium text-text-primary">{t.label}</p>
                    <p className="text-xs text-text-secondary mt-0.5">{t.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="v-desc">Description</Label>
            <textarea
              id="v-desc"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              placeholder="Brief description of services provided…"
              className="flex w-full rounded border border-border-subtle bg-bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan resize-none"
            />
          </div>

          {/* Primary contact (optional) */}
          <div className="rounded-lg border border-border-subtle p-4 space-y-3">
            <p className="text-sm font-medium text-text-primary">Primary contact <span className="text-text-secondary font-normal">(optional)</span></p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="c-first">First name</Label>
                <Input id="c-first" value={contact.first_name} onChange={(e) => setC('first_name', e.target.value)} placeholder="Jane" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-last">Last name</Label>
                <Input id="c-last" value={contact.last_name} onChange={(e) => setC('last_name', e.target.value)} placeholder="Smith" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" type="email" value={contact.email} onChange={(e) => setC('email', e.target.value)} placeholder="jane@acmecorp.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-title">Title / Role</Label>
              <Input id="c-title" value={contact.title} onChange={(e) => setC('title', e.target.value)} placeholder="e.g. IT Security Manager" />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={!form.name || !form.tier || !form.primary_domain || submitting}
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</>
              ) : 'Add Vendor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
